/**
* Licensed Materials - Property of IBM
* IBM Cognos Products: BI Dashboard
* (C) Copyright IBM Corp. 2021, 2022
* US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
*/
define([
	'underscore',
	'../../app/nls/StringResources',
	'../../lib/@waca/dashboard-common/dist/utils/TextEditorConstants',
	'./TextEditorPropertiesUtil'
], (
	_,
	resources,
	TextEditorConstants,
	TextEditorPropertiesUtil
) => {

	const DEFAULT_BANNER_LABEL = resources.get('text_label');
	const DEFAULT_SECTION_LABEL = resources.get('sectionName_details');

	const PROPERTY_ORDERS_V1 = {
		'fontFamily': 1,
		'fontFamilySubClass': 2,
		'fontSize': 3,
		'textColor': 4,
		'textFormat': 5,
		'alignmentPicker': 6,
		'listPicker': 7,
		'verticalAlignPicker': 8,
		'indentationPicker': 9,
		'lineHeight': 10,
		'innerMargins': 11,
		'innerMarginsTop': 12,
		'innerMarginsBottom': 13,
		'innerMarginsLeft': 14,
		'innerMarginsRight': 15
	};

	const FUNCTION_UPDATE_SUFFIX = '_V1';

	class TextProperties {
		constructor ( { refreshCallback, refreshPropertiesCallback, updatePropertiesIfNecessaryCallback }) {
			this.refreshProperties = refreshPropertiesCallback;
			this.refresh = refreshCallback;
			this.updatePropertiesIfNecessary = updatePropertiesIfNecessaryCallback;
		}
	}

	/**
	 * This class is implements the getPropertyLayoutList and getPropertyList functions.
	 * These functions return the set of propery layouts and properties supported by the TextEditor
	 */
	return class TextEditorProperties {
		constructor ({ dashboard, isTextWidgetContext = false, propertiesOptions = {}, textEditor, widget }) {
			this.dashboard = dashboard;
			this.colorsService = this.dashboard.getFeature('Colors');
			this._icons = this.dashboard.getFeature('Icons');
			this.isTextWidgetContext = isTextWidgetContext;
			this.textEditor = textEditor;
			this.widget = widget;
			this.bannerLabel = propertiesOptions.bannerLabel || DEFAULT_BANNER_LABEL;
			this.sectionLabel = propertiesOptions.sectionLabel || DEFAULT_SECTION_LABEL;

			this.textEditor.setTextPropertiesInstance(new TextProperties({
				refreshCallback: this._refreshProperties.bind(this),
				refreshPropertiesCallback: this._refreshPropertiesPane.bind(this),
				updatePropertiesIfNecessaryCallback: this.updatePropertiesIfNecessary.bind(this)
			}));
			this.canvas = this.dashboard.getFeature('Canvas');
			if (this.canvas) {
				this.canvas.on('change:selections:select', this._onChangeSelectionsHandler, this);
				this.canvas.on('change:selections:deselect', this._updateSelectedTextWidgets, this);
			}
		}

		destroy () {
			if (this.canvas) {
				this.canvas.off('change:selections:select', this._onChangeSelectionsHandler, this);
				this.canvas.off('change:selections:deselect', this._updateSelectedTextWidgets, this);
				this.canvas = null;
			}
		}

		/**
		 * Implementation of {@link PropertiesProviderAPI#getPropertyLayoutList}
		 */
		getPropertyLayoutList () {
			if (!this._canSetTextDetailsProperties()) {
				return [];
			}
			const layoutList =  [{
				id: 'textDetail',
				type: 'Group',
				label: resources.get('tabName_textDetails'),
				position: 1
			}, {
				id: 'fontFamily',
				type: 'Section',
				collapsible: false,
				label: resources.get('textFontFamily'),
				position: 1
			}, {
				id: 'fontFamilyLine',
				type: 'SingleLineLinks',
				items: [{
					id: 'left',
					align: 'left',
					items: []
				}, {
					id: 'right',
					align: 'right',
					items: []
				}]
			}, ...this._getV1PropertyLayoutControlList()];

			return layoutList;
		}

		getPropertyList () {
			return [];
		}

		getCustomColorList () {
			const colorList = [];
			const classNames = [];
			const allSpans = this.textEditor.getEditingArea().find('span');
			allSpans.each((index, span) => { classNames.push(span.className); });
			const allClassNames = classNames.join(' ');
			const customColors = this.colorsService.getCustomColorSet();
			customColors.forEach((customColor) => {
				if(allClassNames.indexOf(customColor.id) !== -1){
					colorList.push(customColor.hexValue);
				}
			});
			return colorList;
		}

		isEditing () {
			return this.textEditor.isEditing();
		}

		getEditingActionList () {
			return this.textEditor.getEditingActionList();
		}

		_getV1PropertyLayoutControlList () {
			if (!this._canSetTextDetailsProperties()) {
				return [];
			}

			const layoutControlList = [];
			const currentActiveStyles = this._selectCurrentActiveStyles();

			layoutControlList.push(...this._getFontFamilyProperties(currentActiveStyles));
			layoutControlList.push(this._getFontSizeProperty(currentActiveStyles));
			layoutControlList.push(this._getTextColorProperty(currentActiveStyles));
			layoutControlList.push(this._getFontWeightProperty(currentActiveStyles));
			layoutControlList.push(this._getTextAlignmentProperty(currentActiveStyles));
			if (this.isTextWidgetContext) {
				this._addPropertiesForTextWidget(layoutControlList, currentActiveStyles);
			}
			return layoutControlList;
		}

		_getFontFamilyProperties (currentActiveStyles) {
			const propId = 'fontFamily';
			const { selected, fontFamilyChoices, cssClass, subCssClass } = this._getSelectedFamilyFont(currentActiveStyles);
			return [{
				id: propId,
				editor: {
					sectionId: 'textDetail.fontFamily.fontFamilyLine.left',
					position: PROPERTY_ORDERS_V1[propId],
					uiControl: {
						type: 'DropDown',
						ariaDescribedby: resources.get('textFontFamily'),
						options: fontFamilyChoices,
						responsive: false,
						style: 'width: 156px;',
						value: selected[0],
						onChange: this._getOnStyleChangeCallback({
							fnName: 'onFontFamilyUpdate',
							suffix: FUNCTION_UPDATE_SUFFIX,
							textEditor: this.textEditor,
							isTextWidgetContext: this.isTextWidgetContext
						})
					}
				}
			}, {
				id: 'fontFamilySubClass',
				editor: {
					sectionId: 'textDetail.fontFamily.fontFamilyLine.left',
					position: PROPERTY_ORDERS_V1['fontFamilySubClass'],
					hidden: !cssClass || !cssClass.subCssClasses || cssClass.subCssClasses.length === 0,
					uiControl: {
						type: 'DropDown',
						ariaDescribedby: resources.get('textFontFamily'),
						options: cssClass && cssClass.subCssClasses,
						value: subCssClass && subCssClass.value,
						onChange: this._getOnStyleChangeCallback({
							fnName: 'onFontFamilyUpdate',
							suffix: FUNCTION_UPDATE_SUFFIX,
							textEditor: this.textEditor,
							isTextWidgetContext: this.isTextWidgetContext
						}),
						style: 'width:80px;',
						responsive: false
					}
				}
			}];
		}

		_getSelectedFamilyFont (currentActiveStyles) {
			const activeStyle = currentActiveStyles['font-family'];
			let selected = activeStyle ? TextEditorPropertiesUtil.convertCssToFontId(activeStyle) : [];
			const fontFamilyChoices = this._getFontFamilyChoices();
			let cssClass = TextEditorPropertiesUtil.findFontFamilyItemFromValue(currentActiveStyles, selected[0], fontFamilyChoices);
			if (!cssClass) {
				selected = [ this._getDefaultFont().id ];
				cssClass = TextEditorPropertiesUtil.findFontFamilyItemFromValue(currentActiveStyles, selected[0], fontFamilyChoices);
			}
			const subCssClass = _.find(cssClass.subCssClasses, (subCssClass) => {
				// if subCssClass is given from select, find subCss with given selected value, otherwise find it from cssClass
				return selected.length > 1 ? subCssClass.value === selected[1] : subCssClass.cssClass === cssClass.cssClass;
			});
			return {
				selected: selected,
				fontFamilyChoices: fontFamilyChoices,
				cssClass: cssClass,
				subCssClass: subCssClass
			};
		}

		_getFontSizeProperty (currentActiveStyles) {
			const propId = 'fontSize';
			return {
				id: propId,
				editor: {
					sectionId: 'textDetail',
					position: PROPERTY_ORDERS_V1[propId],
					uiControl: {
						type: 'DropDown',
						label: resources.get('textFontSize'),
						ariaDescribedby: resources.get('textFontSize'),
						options: this.textEditor.getFontSizeChoices(),
						onChange: this._getOnStyleChangeCallback({
							fnName: 'onFontSizeUpdate',
							suffix: FUNCTION_UPDATE_SUFFIX,
							textEditor: this.textEditor,
							isTextWidgetContext: this.isTextWidgetContext
						}),
						value: this.isResponsive ? 'auto' : currentActiveStyles['font-size'] + ''
					}
				}
			};
		}

		_getTextColorProperty (currentActiveStyles) {
			const paletteItems = TextEditorPropertiesUtil.getTextColorChoices(this.colorsService);
			const propId = 'textColor';
			const selected = paletteItems.find(palette => palette.id === currentActiveStyles.color) || {
				name: currentActiveStyles.color,
				value: '',
			};

			return {
				id: propId,
				editor: {
					sectionId: 'textDetail',
					position: PROPERTY_ORDERS_V1[propId],
					uiControl: {
						paletteType: 'DashboardColorSet',
						type: 'ColorPicker',
						showHexValue: true,
						addButton: true,
						label: resources.get('propTextColor'),
						open: false,
						ariaLabel: resources.get('propTextColor'),
						items: paletteItems,
						allowSameSelection: true,
						selectedName: selected ? selected.name : '',
						value: selected ? selected.value : '',
						onChange: this._getOnStyleChangeCallback({
							fnName: 'onTextColorUpdate',
							suffix: FUNCTION_UPDATE_SUFFIX,
							textEditor: this.textEditor,
							isTextWidgetContext: this.isTextWidgetContext
						})
					}
				}
			};
		}

		_getFontWeightProperty (currentActiveStyles) {
			const { sectionId, onChange } = this._getFontWeightPropertyContext();
			const propId = 'textFormat';
			return {
				id: propId,
				editor: {
					sectionId: sectionId,
					position: PROPERTY_ORDERS_V1[propId],
					uiControl: {
						type: 'IconPicker',
						label: resources.get('textStyle'),
						useToggling: true,
						allowMultiple: true,
						items: this.textEditor.getTextFormatOptions(),
						values: currentActiveStyles['text-format'],
						onChange: onChange
					}
				}
			};
		}

		_getFontWeightPropertyContext () {
			return {
				sectionId: 'textDetail',
				onChange: this._getOnStyleChangeCallback({
					fnName: 'onTextFormatUpdate',
					suffix: FUNCTION_UPDATE_SUFFIX,
					textEditor: this.textEditor,
					isTextWidgetContext: this.isTextWidgetContext
				})
			};
		}

		_getTextAlignmentProperty (currentActiveStyles) {
			const propId = 'alignmentPicker';
			const { sectionId, onChange } = this._getTextAlignmentPropertyContext();
			return {
				id: propId,
				editor: {
					sectionId: sectionId,
					position: PROPERTY_ORDERS_V1[propId],
					uiControl: {
						type: 'IconPicker',
						label: resources.get('propAlignPicker'),
						items: [{
							name: 'justifyLeft',
							label: resources.get('propAlignPickLeft'),
							value: 'common-left-align'
						}, {
							name: 'justifyCenter',
							label: resources.get('propAlignPickCenter'),
							value: 'common-center-align'
						}, {
							name: 'justifyRight',
							label: resources.get('propAlignPickRight'),
							value: 'common-right-align'
						}],
						values: currentActiveStyles['text-align'],
						onChange: onChange
					}
				}
			};
		}

		_getTextAlignmentPropertyContext () {
			return {
				sectionId: 'textDetail',
				onChange: this._getOnStyleChangeCallback({
					fnName: 'onTextAlignmentUpdate',
					suffix: FUNCTION_UPDATE_SUFFIX,
					textEditor: this.textEditor,
					isTextWidgetContext: this.isTextWidgetContext
				})
			};
		}

		// Properties for text widget context
		_addPropertiesForTextWidget(layoutListItems, currentActiveStyles) {
			layoutListItems.push(this._getListProperty(currentActiveStyles));
			layoutListItems.push(this._getVerticalAlignProperty());
			layoutListItems.push(this._getIndentationProperty());
			layoutListItems.push(this._getLineHeightProperty(currentActiveStyles));
			layoutListItems.push(...this._getInnerMarginsProperty());
		}

		_getListProperty (currentActiveStyles) {
			let selected;
			const propId = 'listPicker';
			const { sectionId, position, onChange } = this._getListPropertyContext(propId);
			if(currentActiveStyles['insertUnorderedList']) {
				selected = 'insertUnorderedList';
			} else if (currentActiveStyles['insertOrderedList']) {
				selected = 'insertOrderedList';
			}
			return {
				id: propId,
				editor: {
					sectionId: sectionId,
					position: position,
					uiControl: {
						type: 'IconPicker',
						label: resources.get('propList'),
						useToggling: true,
						items: [{
							name: 'insertUnorderedList',
							label: resources.get('propUnorderedList'),
							value: this._icons.getIcon('bullet-list').id
						}, {
							name: 'insertOrderedList',
							label: resources.get('propOrderedList'),
							value: this._icons.getIcon('number-list').id
						}],
						values: selected,
						onChange: onChange
					}
				}
			};
		}

		_getListPropertyContext (propId) {
			return {
				sectionId: 'textDetail',
				position: PROPERTY_ORDERS_V1[propId],
				onChange: this._getOnStyleChangeCallback({
					fnName: 'onListUpdate',
					suffix: FUNCTION_UPDATE_SUFFIX,
					textEditor: this.textEditor,
					isTextWidgetContext: this.isTextWidgetContext
				})
			};
		}

		_getVerticalAlignProperty () {
			const propId = 'verticalAlignPicker';
			const { sectionId, onChange } = this._getVerticalAlignPropertyContext();
			return {
				id: propId,
				editor: {
					sectionId: sectionId,
					position: PROPERTY_ORDERS_V1[propId],
					uiControl: {
						type: 'IconPicker',
						label: resources.get('propVerticalAlignPicker'),
						items: [{
							name: 'top',
							label: resources.get('propAlignPickTop'),
							value: this._icons.getIcon('align-top-center').id
						}, {
							name: 'middle',
							label: resources.get('propAlignPickMiddle'),
							value: this._icons.getIcon('align-middle-center').id
						}, {
							name: 'bottom',
							label: resources.get('propAlignPickBottom'),
							value: this._icons.getIcon('align-bottom-center').id
						}],
						values: this._getVerticalAlignValues(),
						onChange: onChange
					}
				}
			};
		}

		_getVerticalAlignPropertyContext () {
			return {
				sectionId: 'textDetail',
				onChange: this._getOnStyleChangeCallback({
					fnName: 'onVerticalAlignChange',
					suffix: FUNCTION_UPDATE_SUFFIX,
					textEditor: this.textEditor,
					isTextWidgetContext: this.isTextWidgetContext
				})
			};
		}

		_getIndentationProperty () {
			const propId = 'indentationPicker';
			const { sectionId, onChange } = this._getIndentationPropertyContext();
			return {
				id: propId,
				editor: {
					sectionId: sectionId,
					position: PROPERTY_ORDERS_V1[propId],
					uiControl: {
						type: 'IconPicker',
						label: resources.get('textIndentation'),
						items: [{
							name: 'indentMore',
							label: resources.get('textIncreaseIndentation'),
							value: this._icons.getIcon('text-indent-more').id
						}, {
							name: 'indentLess',
							label: resources.get('textDecreaseIndentation'),
							value: this._icons.getIcon('text-indent-less').id
						}],
						onChange: onChange
					}
				}
			};
		}

		_getIndentationPropertyContext () {
			return {
				sectionId: 'textDetail',
				onChange: this._getOnStyleChangeCallback({
					fnName: 'onIndentationChange',
					suffix: FUNCTION_UPDATE_SUFFIX,
					textEditor: this.textEditor,
					isTextWidgetContext: this.isTextWidgetContext
				})
			};
		}

		_getLineHeightProperty (currentActiveStyles) {
			const propId = 'lineHeight';
			const { sectionId, onChange } = this._getLineHeightPropertyContext();
			return {
				id: propId,
				editor: {
					sectionId: sectionId,
					position: PROPERTY_ORDERS_V1[propId],
					uiControl: {
						type: 'DropDown',
						label: resources.get('textLineHeight'),
						ariaDescribedby: resources.get('textLineHeight'),
						options: TextEditorConstants.LINE_HEIGHT_CHOICES,
						onChange: onChange,
						value: this._getLineHeightValue(currentActiveStyles)
					}
				}
			};
		}

		_getLineHeightPropertyContext () {
			return {
				sectionId: 'textDetail',
				onChange: this._getOnStyleChangeCallback({
					fnName: 'onLineHeightChange',
					suffix: FUNCTION_UPDATE_SUFFIX,
					textEditor: this.textEditor,
					isTextWidgetContext: this.isTextWidgetContext
				})
			};
		}

		_getInnerMarginsProperty () {
			const innerMargins = [{
				id: 'innerMargins',
				type: 'Section',
				collapsible: true,
				label: resources.get('textInnerMargins'),
				position: PROPERTY_ORDERS_V1['innerMargins']
			}];

			const marginPositions = ['Top', 'Bottom', 'Left', 'Right'];
			marginPositions.forEach(position => {
				const propId = 'innerMargins' + position;
				const sectionId = 'textDetail.innerMargins';
				const propLabel = resources.get('prop' + position);
				const propPosition = PROPERTY_ORDERS_V1['innerMargins' + position];
				innerMargins.push(this._getInnerMarginSpec(propId, propPosition, propLabel, sectionId));
			});
			return innerMargins;
		}

		_getInnerMarginSpec(propId, propPosition, propLabel, sectionId) {
			const validator = this._validatePositiveNumber.bind(this);
			const marginValue = this.textEditor.getInnerMarginsValue(propId);
			return {
				id: propId,
				defaultValue: 10,
				editor: {
					sectionId: sectionId,
					position: propPosition,
					uiControl: {
						type: 'InputLabel',
						inputType: 'number',
						label: propLabel,
						ariaLabel: propLabel,
						handleReturnKey: true,
						showInlineError: true,
						value: marginValue,
						customValidatorCallback: validator,
						onChange: this._getOnStyleChangeCallback({
							fnName: 'onInnerMarginsChange',
							textEditor: this.textEditor,
							isTextWidgetContext: this.isTextWidgetContext
						}),
						decimalPlaces: 0,
						min: 0,
						multiline: true
					}
				}
			};
		}

		_validatePositiveNumber(value) {
			let ret = {
				isValid: true
			};
			if ( value < 0 || value === '') {
				ret.message = resources.get('notPositiveNumberError');
				ret.isValid = false;
			}
			return ret;
		}

		_getLineHeightValue (activeStyles) {
			let lineHeightValue = activeStyles && activeStyles['line-height'];
			const lineHeightChoices = TextEditorConstants.LINE_HEIGHT_CHOICES;
			if (lineHeightValue && !isNaN(lineHeightValue)) {
				if (!lineHeightValue.includes('.')) {
					// the line-height value saved by summernote trims '.0', add it back here to compare with list of choices
					lineHeightValue += '.0';
				}
				const lhEntry = lineHeightChoices.find(lh => lh.value === lineHeightValue);
				if (lhEntry) {
					return lhEntry.value;
				}
			}
			return lineHeightChoices[0].value;
		}

		/**
		 * gets the default font
		 * @return {Object} font default font properties
		 * @returns {object} the default font
		 */
		_getDefaultFont() {
			return this.textEditor.getDefaultFont();
		}

		_getFontFamilyChoices () {
			if (!this._fontFamilyChoices) {
				this._fontFamilyChoices = this.textEditor.getFontFamilyChoices();
			}
			return this._fontFamilyChoices;
		}

		_getActiveStyles () {
			return this.textEditor.getActiveStyles() || {};
		}

		_getLastActiveStyles () {
			return this.textEditor.getLastActiveStyles() || {};
		}

		_setLastActiveStyles (styles) {
			return this.textEditor.setLastActiveStyles(styles);
		}

		_selectCurrentActiveStyles () {
			return this.textEditor.selectCurrentActiveStyles();
		}

		_getVerticalAlignValues () {
			return this.textEditor.getVerticalAlignValues();
		}

		_supportTextWidgetContext () {
			return this.isTextWidgetContext;
		}

		updatePropertiesIfNecessary (previousActiveStyles) {
			if (this._canSetTextDetailsProperties()) {
				const activeStyles = this._getActiveStyles();
				const lastActiveStyles = previousActiveStyles || this._getLastActiveStyles();
				const filteredActiveStyles = this._filterActiveStyles(activeStyles);
				var filteredLastActiveStyles = this._filterActiveStyles(lastActiveStyles);
				if (!_.isEqual(filteredActiveStyles, filteredLastActiveStyles)) {
					this._setLastActiveStyles(activeStyles);
					this._updateTextToolbar(activeStyles);
					this._refreshPropertiesPane();
				}
			}
		}

		_canSetTextDetailsProperties () {
			return this.textEditor.canSetTextDetailsProperties();
		}

		_getOnStyleChangeCallback ({ fnName, suffix = '', textEditor, isTextWidgetContext }) {
			if (!textEditor) {
				throw new Error ('Invalid textEditor');
			}
			const fnToUpdate = textEditor[`${fnName}${suffix}`].bind(textEditor);
			if(typeof(fnToUpdate) !== 'function') {
				throw new Error(`Function ${fnName}${suffix} does not exist in textEditor` );
			}
			if(typeof(textEditor.updateTextDetailStyle) !== 'function') {
				throw new Error(`Function updateTextDetailStyle does not exist in textEditor` );
			}
			return isTextWidgetContext && !this.isEditing() ? textEditor.updateTextDetailStyle.bind(textEditor, fnToUpdate) : fnToUpdate;
		}

		_filterActiveStyles (stylesToFilter) {
			return this.textEditor.filterActiveStyles(stylesToFilter);
		}

		_updateTextToolbar (activeStyles) {
			this.textEditor.updateTextToolbar(activeStyles);
		}

		_onChangeSelectionsHandler () {
			const selectedContents = this.canvas ? this.canvas.getSelectedContentList() : [];
			const selectedIds = selectedContents.map(content => content.getId());
			// Remove the current editing one when select another widget via multi select
			// when there is more than one widget selected
			if (selectedIds.length > 1) {
				selectedIds.forEach(selectedId => {
					if ((this.textEditor.getContentId() === selectedId) && this.textEditor.isEditing()) {
						this.textEditor.toggleEditing(false);
						if (this.widget.showPlaceHolder) {// WidgetTitleView do not have this function define
							this.widget.showPlaceHolder();
						}
					}
				});
			}
		}

		_updateSelectedTextWidgets () {}

		_refreshPropertiesPane (options) {
			this.widget.refreshPropertiesPane(options);
		}

		_refreshProperties () {
		}
	};
});
